import { useMemo } from "react";

import { useEncounterProcedures } from "@/encounters/contexts/EncounterProceduresContext";
import { EncounterDiagnosisGroup } from "@/encounters/types/procedures";
import { groupEncounterDiagnoses } from "@/encounters/utils/encounter-diagnoses";

export type UseEncounterDiagnoses = {
  isLoading: boolean;
  /** One entry per diagnosis on the encounter, with the procedures recorded under it. */
  diagnoses: EncounterDiagnosisGroup[];
  /** True when the read failed, so an outage is not shown as an encounter with no diagnoses. */
  hasLoadFailed: boolean;
  /** True while the encounter has staged procedure work it has not written yet, from any section. */
  hasStagedChanges: boolean;
};

/**
 * The encounter's diagnoses with their procedures, grouped for the Dx tab's Diagnoses section.
 *
 * The read and the staged work both belong to `EncounterProceduresProvider`, so what this returns is
 * the encounter as its save will leave it: a removal staged from either tab is already gone from
 * these groups, and `hasStagedChanges` says so the same way the procedures tab does.
 *
 * A failed read is reported apart from an empty result — "this encounter has no diagnoses" and "we
 * could not find out" are different things to tell a provider.
 */
function useEncounterDiagnoses(): UseEncounterDiagnoses {
  const { isLoading, data, hasLoadFailed, hasStagedChanges } = useEncounterProcedures();

  const diagnoses = useMemo(
    () => (data ? groupEncounterDiagnoses(data.conditions, data.procedures) : []),
    [data],
  );

  return { isLoading, diagnoses, hasLoadFailed, hasStagedChanges };
}

export default useEncounterDiagnoses;
